/**
 * The pools against each other, normalised so they can be compared at all.
 *
 * A pool holding three thousand and a pool holding six hundred cannot be read
 * off one money axis: the larger line dwarfs the smaller and the chart says
 * only which pool is bigger, which nobody needed a chart for. Every series
 * therefore starts at 100 and the axis is "times what it started with".
 *
 * Filled rather than drawn as a hairline. A 1.5px stroke on a dark ground
 * reads as a scratch; the fill gives the series a shape a reader catches
 * without looking directly at it.
 *
 * Readable at a point, not only as a shape. Moving across it — with a pointer
 * or with the arrow keys — names the moment under the cursor and what each
 * series was worth there. Without that a curve answers "roughly which way" and
 * nothing else, and the reader goes looking for a table.
 *
 * Selecting a pool mutes the others rather than hiding them: the comparison is
 * the point, and a chart with one line left has stopped making it.
 */
import { useId, useLayoutEffect, useRef, useState } from "react";
import { Button } from "@/design-system/Button";

import type { AnalyticsPointResponse } from "@/shared/api";
import { finiteGeometryValue } from "@/shared/money/geometry";

/** The drawing size before the container is measured, and in environments
 *  that cannot measure it. */
const DEFAULT_WIDTH = 640;
const DEFAULT_HEIGHT = 200;
/** Room under the plot for the dates, to its left for the index marks, and
 *  above it so the topmost mark is not clipped by the viewBox. */
const PAD_BOTTOM = 18;
const PAD_LEFT = 34;
const PAD_TOP = 10;

export type IndexSeries = {
  readonly key: string;
  readonly label: string;
  readonly points: readonly AnalyticsPointResponse[];
};

/** One series as an index, or null when it has nothing to normalise against. */
function indexed(series: IndexSeries): readonly number[] | null {
  const raw = series.points.map((point) => finiteGeometryValue(point.value));
  if (raw.length < 2 || raw.some((value) => value === null)) return null;
  const values = raw as readonly number[];
  const [first] = values;
  if (first === undefined) return null;
  // The account's own curve starts at zero PnL, so the base is the magnitude it
  // was measured against rather than the first value: dividing by zero would
  // send the whole series to infinity on the first point.
  const base = first === 0 ? 1 : Math.abs(first);
  return values.map((value) => 100 + ((value - first) / base) * 100);
}

/**
 * Which colour a series takes.
 *
 * The account's own curve is a result, so it uses the result scale — above the
 * 100 it started from is a gain and below it is a loss. Pools take the
 * categorical scale: a pool index is a comparison, and two pools painted green
 * would read as "both in profit" when one may be half as good as the other.
 */
const SERIES_COLOUR = ["text-series-1", "text-series-2", "text-series-3"] as const;

function day(at: string): string {
  return new Date(at).toLocaleDateString("ru-RU", { day: "numeric", month: "short" });
}

function moment(at: string): string {
  return new Date(at).toLocaleString("ru-RU", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function PoolIndexChart({
  series,
  selected,
  onSelect,
}: {
  readonly series: readonly IndexSeries[];
  readonly selected: string | null;
  readonly onSelect: (key: string | null) => void;
}): React.JSX.Element | null {
  const gradient = useId();
  const [at, setAt] = useState<number | null>(null);
  // Drawn at the container's own size, so the chart fills its panel however
  // tall the panel beside it grows, and text is never stretched to fit.
  const frame = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT });
  useLayoutEffect(() => {
    const element = frame.current;
    if (element === null || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry === undefined) return;
      const { width, height } = entry.contentRect;
      if (width > 0 && height > 0) setSize({ width, height });
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, []);
  const WIDTH = size.width;
  const HEIGHT = size.height;

  const drawn = series.flatMap((one) => {
    const values = indexed(one);
    if (values === null) return [];
    const times = one.points.map((point) => Date.parse(point.at));
    return times.some((time) => Number.isNaN(time)) ? [] : [{ ...one, values, times }];
  });
  if (drawn.length === 0) return null;

  const every = drawn.flatMap((one) => one.values);
  const low = Math.min(100, ...every);
  const high = Math.max(100, ...every);
  const span = high - low || 1;
  // One clock for every series. Indexed by its own close count, a pool with
  // four closes ended a third of the way across while the period it covered
  // was the same as the others' (2026-10-08).
  const moments = [...new Set(drawn.flatMap((one) => one.times))].sort((a, b) => a - b);
  const first = moments[0] ?? 0;
  const lastMoment = moments[moments.length - 1] ?? first;
  const range = lastMoment - first || 1;

  const x = (time: number): number =>
    PAD_LEFT + ((time - first) / range) * (WIDTH - PAD_LEFT);
  const y = (value: number): number =>
    HEIGHT - PAD_BOTTOM - ((value - low) / span) * (HEIGHT - PAD_BOTTOM - PAD_TOP);

  const line = (values: readonly number[], times: readonly number[]): string =>
    values
      .map(
        (value, index) =>
          `${index === 0 ? "M" : "L"}${x(times[index] ?? first).toFixed(1)},${y(value).toFixed(1)}`,
      )
      .join(" ");

  /** What a series was worth at a moment: its last value at or before it. */
  const valueAt = (
    one: { readonly values: readonly number[]; readonly times: readonly number[] },
    time: number,
  ): number | undefined => {
    let found: number | undefined;
    one.times.forEach((when, index) => {
      if (when <= time) found = one.values[index];
    });
    return found;
  };

  /** Four marks across the range, so a reader can read a level off the plot. */
  const marks = [low, low + span / 3, low + (span * 2) / 3, high];

  const hovered = at === null ? null : Math.min(Math.max(0, at), moments.length - 1);
  const hoveredTime = hovered === null ? null : (moments[hovered] ?? null);

  return (
    <figure
      className="relative flex min-h-0 min-w-0 flex-1 flex-col gap-2"
      data-testid="pool-index-chart"
    >
      <div ref={frame} className="relative min-h-48 flex-1">
      <svg
        role="img"
        aria-label="Динамика пулов, индекс 100"
        viewBox={`0 0 ${String(WIDTH)} ${String(HEIGHT)}`}
        tabIndex={0}
        data-testid="pool-index-surface"
        className="absolute inset-0 h-full w-full focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring"
        onMouseMove={(event) => {
          const box = event.currentTarget.getBoundingClientRect();
          const inside = ((event.clientX - box.left) / box.width) * WIDTH;
          const time = first + ((inside - PAD_LEFT) / (WIDTH - PAD_LEFT)) * range;
          // The nearest moment anything closed, so the reading names a real one.
          let nearest = 0;
          moments.forEach((when, index) => {
            if (Math.abs(when - time) < Math.abs((moments[nearest] ?? 0) - time)) {
              nearest = index;
            }
          });
          setAt(nearest);
        }}
        onMouseLeave={() => {
          setAt(null);
        }}
        onKeyDown={(event) => {
          // The same reading by keyboard. A figure reachable only by pointer is
          // a figure the accessibility floor says is not there at all.
          if (event.key === "ArrowRight") {
            setAt((was) => Math.min(moments.length - 1, (was ?? -1) + 1));
          } else if (event.key === "ArrowLeft") {
            setAt((was) => Math.max(0, (was ?? moments.length) - 1));
          } else if (event.key === "Escape") {
            setAt(null);
          } else {
            return;
          }
          event.preventDefault();
        }}
      >
        {marks.map((mark) => (
          <g key={mark}>
            <line
              x1={PAD_LEFT}
              x2={WIDTH}
              y1={y(mark)}
              y2={y(mark)}
              strokeWidth={1}
              strokeDasharray="3 4"
              vectorEffect="non-scaling-stroke"
              className="stroke-chart-grid"
            />
            <text
              x={PAD_LEFT - 6}
              y={y(mark) + 3}
              textAnchor="end"
              className="fill-text-muted text-[10px]"
            >
              {mark.toFixed(0)}
            </text>
          </g>
        ))}

        {drawn.map((one, index) => {
          const muted = selected !== null && one.key !== selected;
          const last100 = one.values[one.values.length - 1] ?? 100;
          const colour =
            one.key === "total"
              ? last100 >= 100
                ? "text-chart-positive"
                : "text-chart-negative"
              : (SERIES_COLOUR[Math.max(0, index - 1) % SERIES_COLOUR.length] ??
                "text-series-1");
          const lastAt = one.times[one.times.length - 1] ?? first;
          const firstAt = one.times[0] ?? first;
          return (
            <g key={one.key} className={muted ? "text-border-strong opacity-40" : colour}>
              {/* Defined inside the coloured group: `currentColor` resolves
                  against the element the stop sits in, so a definition in a
                  bare <defs> would take the svg's colour and every fill would
                  come out the same grey. */}
              <defs>
                <linearGradient id={`${gradient}-${one.key}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="currentColor" stopOpacity={0.28} />
                  <stop offset="100%" stopColor="currentColor" stopOpacity={0} />
                </linearGradient>
              </defs>
              {!muted && (
                <path
                  d={`${line(one.values, one.times)} L${x(lastAt).toFixed(1)},${String(HEIGHT - PAD_BOTTOM)} L${x(firstAt).toFixed(1)},${String(HEIGHT - PAD_BOTTOM)} Z`}
                  fill={`url(#${gradient}-${one.key})`}
                  stroke="none"
                />
              )}
              <path
                d={line(one.values, one.times)}
                fill="none"
                strokeWidth={one.key === selected ? 2 : 1.5}
                vectorEffect="non-scaling-stroke"
                data-series={one.key}
                data-muted={muted ? "true" : "false"}
                className="stroke-current"
              />
            </g>
          );
        })}

        {hoveredTime !== null && (
          <g data-testid="pool-index-cursor">
            <line
              x1={x(hoveredTime)}
              x2={x(hoveredTime)}
              y1={PAD_TOP}
              y2={HEIGHT - PAD_BOTTOM}
              strokeWidth={1}
              strokeDasharray="3 3"
              vectorEffect="non-scaling-stroke"
              className="stroke-border-strong"
            />
            {drawn.map((one) => {
              const value = valueAt(one, hoveredTime);
              return value === undefined ? null : (
                <circle
                  key={one.key}
                  cx={x(hoveredTime)}
                  cy={y(value)}
                  r={3}
                  strokeWidth={2}
                  vectorEffect="non-scaling-stroke"
                  className="fill-background-base stroke-current"
                />
              );
            })}
          </g>
        )}

        {moments.length > 1 && (
          <>
            <text
              x={PAD_LEFT}
              y={HEIGHT - 4}
              textAnchor="start"
              className="fill-text-muted text-[10px]"
            >
              {day(new Date(first).toISOString())}
            </text>
            <text
              x={WIDTH}
              y={HEIGHT - 4}
              textAnchor="end"
              className="fill-text-muted text-[10px]"
            >
              {day(new Date(lastMoment).toISOString())}
            </text>
          </>
        )}
      </svg>
      </div>

      {hoveredTime !== null && (
        <div
          role="status"
          data-testid="pool-index-readout"
          // Pinned rather than following the cursor: a box that moves with the
          // pointer covers the part of the curve being pointed at.
          className="pointer-events-none absolute top-1 right-1 rounded-panel border border-border-subtle bg-background-elevated px-2.5 py-2"
        >
          <p className="text-helper-sm text-text-muted">
            {moment(new Date(hoveredTime).toISOString())}
          </p>
          <ul className="mt-1 flex flex-col gap-0.5">
            {drawn.map((one) => {
              const value = valueAt(one, hoveredTime);
              return value === undefined ? null : (
                <li key={one.key} className="flex items-baseline justify-between gap-4">
                  <span className="text-helper-sm text-text-secondary">{one.label}</span>
                  <span className="text-body-main tabular-nums text-text-primary">
                    {value.toFixed(1)}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <figcaption className="flex flex-wrap gap-x-4 gap-y-1">
        {drawn.map((one, index) => (
          <Button
            key={one.key}
            variant="ghost"
            size="sm"
            aria-pressed={one.key === selected}
            data-testid={`series-${one.key}`}
            onClick={() => {
              onSelect(one.key === selected ? null : one.key);
            }}
            className="min-h-6 gap-1.5 px-1 text-text-muted aria-pressed:text-text-primary"
          >
            <span
              aria-hidden="true"
              className={`h-0.5 w-4 shrink-0 ${
                one.key !== "total"
                  ? (["bg-series-1", "bg-series-2", "bg-series-3"][
                      Math.max(0, index - 1) % 3
                    ] ?? "bg-series-1")
                  : (one.values[one.values.length - 1] ?? 100) >= 100
                    ? "bg-chart-positive"
                    : "bg-chart-negative"
              }`}
            />
            {one.label}
          </Button>
        ))}
      </figcaption>
    </figure>
  );
}
